/**
 * Preference Category
 *
 * Collapsible section grouping notification types by category.
 */

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { PreferenceRow } from "./PreferenceRow";
import {
  CATEGORY_NAMES,
  type NotificationType,
  type NotificationTypePreference,
} from "@/hooks/useNotificationPreferences";
import { cn } from "@/lib/utils";

interface PreferenceCategoryProps {
  category: string;
  types: NotificationType[];
  globalDisabled?: boolean;
  onChange: (
    typeKey: string,
    updates: Partial<NotificationTypePreference>,
  ) => void;
}

export function PreferenceCategory({
  category,
  types,
  globalDisabled,
  onChange,
}: PreferenceCategoryProps) {
  const [isOpen, setIsOpen] = useState(true);

  const categoryName = CATEGORY_NAMES[category] ?? category;
  const enabledCount = types.filter((t) => {
    const override = t.userOverride.enabled;
    return override !== undefined ? override : t.defaultEnabled;
  }).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className="border overflow-hidden"
        style={{
          borderColor: "hsl(var(--border))",
          borderRadius: "8px",
        }}
      >
        {/* Category header */}
        <CollapsibleTrigger
          className={cn(
            "flex items-center justify-between w-full",
            "px-4 py-3 bg-muted/50",
            "hover:bg-accent/30 transition-colors",
            "text-left",
          )}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{categoryName}</span>
            <span className="text-xs text-muted-foreground">
              ({enabledCount} of {types.length} enabled)
            </span>
          </div>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-muted-foreground transition-transform",
              isOpen && "rotate-180",
            )}
          />
        </CollapsibleTrigger>

        {/* Type rows */}
        <CollapsibleContent>
          <div>
            {types.map((type) => (
              <PreferenceRow
                key={type.typeKey}
                type={type}
                disabled={globalDisabled}
                onChange={onChange}
              />
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
